import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";

const COLORS = [
  { name: "KATLAS Ink", hex: "#102C42", role: "Primary text, inverted surfaces, borders (via opacity).", dark: true },
  { name: "KATLAS Stone", hex: "#F6F4EF", role: "Primary canvas. Calm, spacious base.", dark: false },
  { name: "KATLAS Teal", hex: "#167F78", role: "Primary interaction, active states, CTA.", dark: true },
  { name: "KATLAS Proof Gold", hex: "#A97A32", role: "Semantic exception — receipts, attestation, verified evidence only.", dark: true },
];

const CAR_ROWS = [
  { k: "Custody", q: "Who controls the evidence?", i: "Evidence remains with the accountable role, organisation, device, patient, member, shipment or system of record." },
  { k: "Authority", q: "Who is allowed to act?", i: "The acting role must operate within a declared policy, mandate, scope and purpose." },
  { k: "Receipts", q: "What proves it happened?", i: "A receipt records who acted, when, under what authority, which evidence references were used and what was withheld." },
];

const HIERARCHY = ["KATLAS Technology", "KATLAS operational-governance architecture", "Cierge Design Studio — applied journey, discovery, design & pilot", "Journey Storybooks & sector demonstrations"];

const ENV_REQUIRED = [
  "KATLAS_MODE=live",
  "KATLAS_NODE_URL=https://katlasnet.katlastechnology.io/api",
  "KATLAS_BEARER_TOKEN=  (backend-only · blank ⇒ unconfigured)",
  "KATLAS_ALLOW_SIMULATION_FALLBACK=false",
  "KATLAS_TIMEOUT_SECONDS=6",
];

const ENV_PROHIBITED = ["KATLAS_API_KEY", "KATLAS_API_TOKEN", "KATLAS_API_BASE_URL"];

function Panel({ label, children, className = "" }) {
  return (
    <section className={`border border-ink/15 ${className}`}>
      <div className="border-b border-ink/12 px-6 py-4">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.24em] text-ink/55">{label}</h2>
      </div>
      <div className="p-6 md:p-8">{children}</div>
    </section>
  );
}

export default function Foundation() {
  useEffect(() => {
    const prev = document.title;
    document.title = "KATLAS Foundation — Internal Review (v1.1)";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.title = prev;
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-stone text-ink">
      {/* Internal header */}
      <header className="no-print sticky top-0 z-40 flex items-center justify-between border-b border-ink/15 bg-stone/95 px-6 py-4 backdrop-blur-sm md:px-12">
        <div className="flex items-baseline gap-3">
          <span className="font-serif text-lg">KATLAS Foundation</span>
          <span className="border border-gold/45 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
            Internal · v1.1
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            data-testid="foundation-print"
            className="inline-flex items-center gap-2 border border-ink/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink transition-colors hover:border-ink"
          >
            <Printer className="h-4 w-4" strokeWidth={1.5} /> Print / PDF
          </button>
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink/60 hover:text-ink">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Site
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-14 md:px-12" data-testid="foundation-page">
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-teal">Design & governance review</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight md:text-5xl">
          The applied KATLAS foundation.
        </h1>
        <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-ink/70 md:text-lg">
          A single reference for the applied visual system, approved KATLAS
          language, CAR definition, product hierarchy, evidence boundaries and
          safe environment-variable rules. Not a live diagnostics page. Excluded
          from public navigation, search indexing and the sitemap.
        </p>

        <div className="mt-12 space-y-8">
          {/* Typography */}
          <Panel label="Typography">
            <div className="space-y-8">
              <div>
                <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">Editorial serif — Georgia</p>
                <p className="font-serif text-4xl leading-tight">Custody, Authority, Receipts</p>
              </div>
              <div>
                <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">Body / UI — Inter</p>
                <p className="font-sans text-lg text-ink/80">
                  Institutional policy and authorised roles decide. KATLAS makes
                  the decision boundary executable, verifiable and reusable.
                </p>
              </div>
              <div>
                <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">Proof / code — IBM Plex Mono (receipts, hashes, env only)</p>
                <p className="font-mono text-sm text-gold">receipt 0x9f3a…c721 · katlas:att:7H2Q-XR9 · verified</p>
              </div>
            </div>
          </Panel>

          {/* Colour tokens */}
          <Panel label="Colour tokens">
            <div className="grid grid-cols-1 gap-px border border-ink/12 bg-ink/12 sm:grid-cols-2 lg:grid-cols-4">
              {COLORS.map((c) => (
                <div key={c.hex} className="bg-stone">
                  <div className="h-24 w-full" style={{ backgroundColor: c.hex }} />
                  <div className="p-4">
                    <p className="font-serif text-base">{c.name}</p>
                    <p className="mt-1 font-mono text-xs text-gold">{c.hex}</p>
                    <p className="mt-3 font-sans text-xs leading-relaxed text-ink/60">{c.role}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 font-sans text-xs leading-relaxed text-ink/55">
              Neutral borders and secondary text are derived through opacity from
              Ink, Stone or Teal. No independent greys. No gradients. Proof Gold is
              a semantic exception — receipts, attestation and verified evidence only.
            </p>
          </Panel>

          {/* CAR */}
          <Panel label="CAR — do not redefine">
            <div className="divide-y divide-ink/12 border-y border-ink/12">
              {CAR_ROWS.map((r) => (
                <div key={r.k} className="grid grid-cols-1 gap-4 py-5 md:grid-cols-12">
                  <div className="md:col-span-3">
                    <p className="font-serif text-xl">{r.k}</p>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-teal">{r.q}</p>
                  </div>
                  <p className="font-sans text-sm text-ink/70 md:col-span-9">{r.i}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 border border-gold/40 p-5">
              <p className="font-mono text-sm leading-relaxed text-gold">
                Custody controls the evidence. Authority governs the action.
                Receipts prove what happened.
              </p>
            </div>
          </Panel>

          {/* Hierarchy */}
          <Panel label="Product hierarchy">
            <ol className="space-y-3">
              {HIERARCHY.map((h, i) => (
                <li key={h} className="flex items-baseline gap-4" style={{ paddingLeft: `${i * 1.5}rem` }}>
                  <span className="font-mono text-xs text-teal">{`0${i + 1}`}</span>
                  <span className="font-serif text-lg text-ink md:text-xl">{h}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 font-sans text-sm text-ink/60">
              Operating principle — The website explains the engineering architecture. The Cierge
              Design Studio shapes and proves the journey. Receipts evidence what
              happened.
            </p>
          </Panel>

          {/* Environment rules */}
          <Panel label="Safe environment-variable rules" className="border-gold/40">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-teal">Required (backend-only)</p>
            <div className="mt-3 border border-ink/15 bg-ink p-5">
              {ENV_REQUIRED.map((e) => (
                <p key={e} className="font-mono text-xs leading-relaxed text-stone/85">{e}</p>
              ))}
            </div>
            <p className="mt-5 font-sans text-sm leading-relaxed text-ink/70">
              The bearer token is backend-only. It must never appear in frontend
              code, logs, screenshots, public routes, browser responses, GitHub or
              chat prompts. If it is absent or blank, state verbatim:
              <span className="mt-2 block font-mono text-xs text-gold">
                “KATLAS unconfigured — production credential not set.”
              </span>
            </p>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-teal">Prohibited legacy aliases</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {ENV_PROHIBITED.map((e) => (
                <span key={e} className="border border-ink/25 px-3 py-1.5 font-mono text-xs text-ink/50 line-through">
                  {e}
                </span>
              ))}
            </div>
            <p className="mt-4 font-sans text-xs leading-relaxed text-ink/55">
              v1.1 has precedence over the older Cierge Foundation Markdown. The
              legacy KATLAS_API_KEY / KATLAS_API_TOKEN / KATLAS_API_BASE_URL
              instructions must not be used.
            </p>
          </Panel>

          {/* Evidence boundary */}
          <Panel label="Evidence boundary — public-safe receipt">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="border border-gold/40 p-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold">May expose publicly</p>
                <p className="mt-3 font-mono text-xs leading-relaxed text-ink/70">
                  receipt hash · attestation ref · signer ref · node timestamp ·
                  verification status · selected shared evidence refs · withheld count
                </p>
              </div>
              <div className="border border-ink/20 p-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Must not expose</p>
                <p className="mt-3 font-mono text-xs leading-relaxed text-ink/70">
                  bearer token · Authorization header · full signed-receipt envelope ·
                  raw personal data · withheld assets · payment details · safeguarding
                  notes · raw prompts
                </p>
              </div>
            </div>
          </Panel>
        </div>

        <p className="mt-12 font-mono text-[11px] text-ink/40">
          KATLAS / Cierge Labs Orchestration Briefing Baseline v1.1 · internal reusable baseline
        </p>
      </div>
    </div>
  );
}
